// =============================================================================
// File Validation and Processing Utilities
// =============================================================================

/**
 * File validation utilities for upload system
 */

// Supported file types and their configurations
export const FILE_TYPES = {
  IMAGES: {
    'image/jpeg': { extensions: ['.jpg', '.jpeg'], maxSize: 10 * 1024 * 1024 },
    'image/png': { extensions: ['.png'], maxSize: 10 * 1024 * 1024 },
    'image/gif': { extensions: ['.gif'], maxSize: 5 * 1024 * 1024 },
    'image/webp': { extensions: ['.webp'], maxSize: 10 * 1024 * 1024 }
  },
  VIDEOS: {
    'video/mp4': { extensions: ['.mp4'], maxSize: 100 * 1024 * 1024 },
    'video/avi': { extensions: ['.avi'], maxSize: 100 * 1024 * 1024 },
    'video/mov': { extensions: ['.mov'], maxSize: 100 * 1024 * 1024 },
    'video/wmv': { extensions: ['.wmv'], maxSize: 100 * 1024 * 1024 }
  },
  DOCUMENTS: {
    'application/pdf': { extensions: ['.pdf'], maxSize: 25 * 1024 * 1024 },
    'application/msword': { extensions: ['.doc'], maxSize: 25 * 1024 * 1024 },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { 
      extensions: ['.docx'], 
      maxSize: 25 * 1024 * 1024 
    }
  }
};

// File validation presets for different use cases
export const VALIDATION_PRESETS = {
  EVIDENCE_PHOTOS: {
    allowedTypes: FILE_TYPES.IMAGES,
    maxFiles: 10,
    maxTotalSize: 50 * 1024 * 1024,
    requireGPS: true
  },
  VERIFICATION_VIDEOS: {
    allowedTypes: { ...FILE_TYPES.IMAGES, ...FILE_TYPES.VIDEOS },
    maxFiles: 5,
    maxTotalSize: 200 * 1024 * 1024,
    requireGPS: true
  },
  CONTRACTS_DOCUMENTS: {
    allowedTypes: { ...FILE_TYPES.DOCUMENTS, ...Object.fromEntries(Object.entries(FILE_TYPES.IMAGES).slice(0, 2)) },
    maxFiles: 15,
    maxTotalSize: 100 * 1024 * 1024,
    requireGPS: false
  },
  PROFILE_PHOTOS: {
    allowedTypes: FILE_TYPES.IMAGES,
    maxFiles: 1,
    maxTotalSize: 5 * 1024 * 1024,
    requireGPS: false
  }
};

/**
 * Validate a single file
 */
export const validateFile = (file, validationConfig = {}) => {
  const {
    allowedTypes = { ...FILE_TYPES.IMAGES, ...FILE_TYPES.VIDEOS, ...FILE_TYPES.DOCUMENTS },
    maxFileSize = 25 * 1024 * 1024,
    requireGPS = false
  } = validationConfig;

  const errors = [];

  // Check file type
  const fileTypeConfig = allowedTypes[file.type];
  if (!fileTypeConfig) {
    errors.push(`File type '${file.type}' is not allowed`);
  } else {
    // Check file size against type-specific limit
    const maxSize = fileTypeConfig.maxSize || maxFileSize;
    if (file.size > maxSize) {
      errors.push(`File size (${formatFileSize(file.size)}) exceeds limit (${formatFileSize(maxSize)})`);
    }
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = Object.values(allowedTypes).some(config =>
    config.extensions.some(ext => fileName.endsWith(ext.toLowerCase()))
  );

  if (!hasValidExtension) {
    errors.push(`File extension is not supported`);
  }

  // Check for potentially malicious files
  if (file.name.includes('../') || file.name.includes('..\\')) {
    errors.push('File name contains invalid characters');
  }

  // Check if file is empty
  if (file.size === 0) {
    errors.push('File is empty');
  }

  return {
    isValid: errors.length === 0,
    errors,
    fileInfo: {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    }
  };
};

/**
 * Validate multiple files
 */
export const validateFiles = (files, validationConfig = {}) => {
  const {
    maxFiles = 10,
    maxTotalSize = 100 * 1024 * 1024,
    ...fileValidationConfig
  } = validationConfig;

  const results = [];
  let totalSize = 0;
  const errors = [];

  // Check file count
  if (files.length > maxFiles) {
    errors.push(`Too many files selected. Maximum allowed: ${maxFiles}`);
  }

  // Validate each file and calculate total size
  files.forEach((file, index) => {
    const validation = validateFile(file, fileValidationConfig);
    results.push({
      index,
      file,
      ...validation
    });

    if (validation.isValid) {
      totalSize += file.size;
    }
  });

  // Check total size
  if (totalSize > maxTotalSize) {
    errors.push(`Total file size (${formatFileSize(totalSize)}) exceeds limit (${formatFileSize(maxTotalSize)})`);
  }

  // Check for duplicate file names
  const fileNames = files.map(f => f.name.toLowerCase());
  const duplicates = fileNames.filter((name, index) => fileNames.indexOf(name) !== index);
  if (duplicates.length > 0) {
    errors.push(`Duplicate file names found: ${[...new Set(duplicates)].join(', ')}`);
  }

  return {
    isValid: errors.length === 0 && results.every(r => r.isValid),
    errors,
    results,
    totalSize,
    validFiles: results.filter(r => r.isValid).map(r => r.file),
    invalidFiles: results.filter(r => !r.isValid)
  };
};

/**
 * Process file for upload (resize images, generate thumbnails, etc.)
 */
export const processFileForUpload = async (file, options = {}) => {
  const {
    maxImageWidth = 1920,
    maxImageHeight = 1080,
    imageQuality = 0.8,
    generateThumbnail = true,
    thumbnailSize = 200
  } = options;

  try {
    if (file.type.startsWith('image/')) {
      return await processImage(file, {
        maxWidth: maxImageWidth,
        maxHeight: maxImageHeight,
        quality: imageQuality,
        generateThumbnail,
        thumbnailSize
      });
    }

    // For non-images, return file info without processing
    return {
      originalFile: file,
      processedFile: file,
      thumbnail: null,
      metadata: await extractFileMetadata(file)
    };
  } catch (error) {
    console.error('Error processing file:', error);
    throw new Error(`Failed to process file: ${error.message}`);
  }
};

/**
 * Process image file (resize, compress, generate thumbnail)
 */
const processImage = (file, options) => {
  return new Promise((resolve, reject) => {
    const { maxWidth, maxHeight, quality, generateThumbnail, thumbnailSize } = options;
    
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = async () => {
      try {
        // Calculate new dimensions
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspectRatio;
        }
        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }

        // Resize main image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(async (processedBlob) => {
          if (!processedBlob) {
            reject(new Error('Failed to process image'));
            return;
          }

          // Create processed file
          const processedFile = new File([processedBlob], file.name, {
            type: file.type,
            lastModified: file.lastModified
          });

          let thumbnail = null;
          
          // Generate thumbnail if requested
          if (generateThumbnail) {
            const thumbCanvas = document.createElement('canvas');
            const thumbCtx = thumbCanvas.getContext('2d');
            
            thumbCanvas.width = thumbnailSize;
            thumbCanvas.height = thumbnailSize;
            
            // Calculate thumbnail dimensions (square crop from center)
            const size = Math.min(width, height);
            const x = (width - size) / 2;
            const y = (height - size) / 2;
            
            thumbCtx.drawImage(canvas, x, y, size, size, 0, 0, thumbnailSize, thumbnailSize);
            
            thumbCanvas.toBlob((thumbBlob) => {
              if (thumbBlob) {
                thumbnail = new File([thumbBlob], `thumb_${file.name}`, {
                  type: file.type,
                  lastModified: file.lastModified
                });
              }
              
              resolve({
                originalFile: file,
                processedFile,
                thumbnail,
                metadata: {
                  originalSize: { width: img.width, height: img.height },
                  processedSize: { width, height },
                  compressionRatio: file.size / processedFile.size
                }
              });
            }, file.type, quality);
          } else {
            resolve({
              originalFile: file,
              processedFile,
              thumbnail: null,
              metadata: {
                originalSize: { width: img.width, height: img.height },
                processedSize: { width, height },
                compressionRatio: file.size / processedFile.size
              }
            });
          }
        }, file.type, quality);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Extract metadata from files
 */
export const extractFileMetadata = async (file) => {
  const metadata = {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: new Date(file.lastModified),
    extension: file.name.split('.').pop()?.toLowerCase()
  };

  // Try to extract EXIF data for images
  if (file.type.startsWith('image/')) {
    try {
      const exifData = await extractExifData(file);
      metadata.exif = exifData;
    } catch (error) {
      console.warn('Could not extract EXIF data:', error);
    }
  }

  return metadata;
};

/**
 * Extract EXIF data from images (GPS, camera info, etc.)
 */
const extractExifData = (file) => {
  return new Promise((resolve) => {
    // This is a simplified version - in production, you'd use a library like 'exif-js' or 'piexifjs'
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        // Basic EXIF extraction would go here
        // For now, return basic info
        resolve({
          hasGPS: false, // Would extract GPS coordinates if available
          cameraInfo: null,
          timestamp: file.lastModified
        });
      } catch (error) {
        resolve({});
      }
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Generate unique file name to prevent conflicts
 */
export const generateUniqueFileName = (originalName, userId, category = 'evidence') => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const random = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop();
  const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
  
  return `${category}/${userId}/${timestamp}_${random}_${nameWithoutExt}.${extension}`;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if file is an image
 */
export const isImage = (file) => {
  return file.type.startsWith('image/');
};

/**
 * Check if file is a video
 */
export const isVideo = (file) => {
  return file.type.startsWith('video/');
};

/**
 * Check if a stored media URL points to a video, by file extension.
 * Handles presigned S3 URLs (which carry a ?query string) and data/blob URLs.
 */
const VIDEO_URL_EXTENSIONS = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.3gp', '.quicktime', '.m4v'];
export const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('data:video')) return true;
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  return VIDEO_URL_EXTENSIONS.some((ext) => path.endsWith(ext));
};

/**
 * Check if file is a document
 */
export const isDocument = (file) => {
  return file.type.includes('pdf') || 
         file.type.includes('word') || 
         file.type.includes('document');
};

/**
 * Create file preview URL (for images/videos)
 */
export const createPreviewUrl = (file) => {
  if (isImage(file) || isVideo(file)) {
    return URL.createObjectURL(file);
  }
  return null;
};

/**
 * Cleanup preview URLs to prevent memory leaks
 */
export const cleanupPreviewUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};